import { enqueueSnackbar } from 'notistack';
import {
    useCancelVisitMutation,
    usePresentVisitMutation,
    useSendVisitToRevisionMutation
} from '../../../services/visitasApi';
import { handleErrors } from '../../../utils/common';
import useAlerts from './useAlerts';
import { useTranslation } from 'react-i18next';
import useTranslate from '../../../hooks/useTranslate';
import { useAppDispatch } from '../../../hooks/reduxHooks';
import { showAlert } from '../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../constants/values';

const useVisitActions = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation('visitas');
    const { translate } = useTranslate(t);

    const {
        showPresentVisitConfirmationAlert,
        showCancelVisitConfirmationAlert,
        showRejectToRevisionConfirmationAlert
    } = useAlerts({
        translate
    });

    const [presentVisit, { isLoading: isPresentLoading }] = usePresentVisitMutation();
    const [cancelVisit, { isLoading: isCancelLoading }] = useCancelVisitMutation();
    const [sendVisitToRevision, { isLoading: isSendToRevisionLoading }] =
        useSendVisitToRevisionMutation();

    const handlePresentVisitClick = (visitId: number) => {
        showPresentVisitConfirmationAlert({
            confirmAction: () => handlePresentVisit(visitId)
        });
    };

    const handleCancelVisitClick = (visitId: number) => {
        showCancelVisitConfirmationAlert({
            confirmAction: (observacion: string) => handleCancelVisit(visitId, observacion)
        });
    };

    const handleSendVisitToRevisionClick = (
        visitId: number,
        observation: string,
        closeDrawer: () => void
    ) => {
        showRejectToRevisionConfirmationAlert({
            confirmAction: () => handleSendVisitToRevision(visitId, observation, closeDrawer)
        });
    };

    const handlePresentVisit = async (visitId: number) => {
        try {
            if (!visitId) return;

            const response: any = await presentVisit({ visitId: visitId }).unwrap();

            if (response?.error) {
                handleErrors(response?.error);
                return;
            }

            enqueueSnackbar('Estado editado con Éxito.', { variant: 'success' });
        } catch (error: any) {
            dispatch(
                showAlert({
                    title: 'No se pudo presentar la visita',
                    message: error?.data?.message || ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleCancelVisit = async (visitId: number, observacion: string) => {
        try {
            if (!visitId) return;

            const response: any = await cancelVisit({ visitId, observacion }).unwrap();

            if (response?.error) {
                handleErrors(response?.error);
                return;
            }
            enqueueSnackbar('La visita fue cancelada con éxito.', { variant: 'success' });
        } catch (error: any) {
            dispatch(
                showAlert({
                    title: 'No se pudo cancelar la visita',
                    message: error?.data?.message || ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleSendVisitToRevision = async (
        visitId: number,
        observation: string,
        closeDrawer: () => void
    ) => {
        try {
            if (!visitId) return;

            const response: any = await sendVisitToRevision({
                visitId: visitId,
                body: { observacion: observation }
            }).unwrap();

            if (response?.error) {
                handleErrors(response?.error);
                return;
            }
            enqueueSnackbar('La visita fue enviada al preventor.', { variant: 'success' });
            closeDrawer();
        } catch (error: any) {
            dispatch(
                showAlert({
                    title: 'No se pudo enviar la visita al preventor',
                    message: error?.data?.message || ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    return {
        handlePresentVisitClick,
        handleCancelVisitClick,
        handleSendVisitToRevisionClick,
        isPresentLoading,
        isCancelLoading,
        isSendToRevisionLoading
    };
};

export default useVisitActions;
